import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Summary } from '../types';
import { formatCurrency } from 'support/helpers/currency/currency';
import { useDocumentCurrency } from 'components/WebEDI/helpers';
import { countDecimalPlaces } from 'support/helpers/numbers/numbers';
import { RoundedCurrencyTooltip } from '../RoundedCurrency';

type OrderResponseSummaryValues = {
  totalQuantity: number;
  subtotal: number;
  charges: number;
  allowances: number;
  taxes: number;
  due: number;
  net: number;
};

const calculateLineItemsQuantity = (lineItems: Array<any> | undefined): number => {
  return (
    lineItems?.reduce((acc, item) => {
      return acc + (item.confirmed_quantity ?? 0);
    }, 0) || 0
  );
};

// We are calculating a more user-friendly summary for the invoice here:
// Basically we are reporting charges and allowances separately from the subtotal
// regardless of whether they are on line- or document level.
// Typically the subtotal would already have charges and allowances from the line-level included.
const calculateInvoiceAndCreditNoteSummary = (
  initialSummary: OrderResponseSummaryValues,
  lineItems?: Array<any>,
): OrderResponseSummaryValues =>
  lineItems?.reduce(
    (acc, item) => ({
      ...acc,
      subtotal: acc.subtotal + item.line_gross_amount,
      charges: acc.charges + item.line_charge_amount,
      allowances: acc.allowances + item.line_allowance_amount,
    }),
    initialSummary,
  ) ?? initialSummary;

const useOrderResponseSummaryValues = (): OrderResponseSummaryValues => {
  const [lineItems, due, net, taxes, charges, allowances] = useWatch({
    name: [
      'line_items',
      'info.0.due_amount',
      'info.0.net_amount',
      'tax.total_tax_amount',
      'info.0.charge_amount',
      'info.0.allowance_amount',
    ],
  });

  return useMemo(
    () =>
      calculateInvoiceAndCreditNoteSummary(
        {
          subtotal: 0,
          charges: charges ?? 0,
          allowances: allowances ?? 0,
          taxes: taxes ?? 0,
          due: due ?? 0,
          net: net ?? 0,
          totalQuantity: calculateLineItemsQuantity(lineItems),
        },
        lineItems,
      ),
    [charges, allowances, taxes, due, net, lineItems],
  );
};

export const useOrderResponseSummary = (): Summary => {
  const { t } = useTranslation();
  const currency = useDocumentCurrency();
  const values = useOrderResponseSummaryValues();

  return useMemo(() => {
    return {
      content: [
        [
          {
            label: t('webedi:documentSummary.orderResponse.totalQuantity.label'),
            value: Math.round(values.totalQuantity),
          },
          {
            label: t(`webedi:documentSummary.orderResponse.grossAmount.label`),
            value: <span className="tabular-nums">{formatCurrency(values.subtotal, currency)}</span>,
          },
          {
            label: t(`webedi:documentSummary.orderResponse.allowances.label`),
            value: <span className="tabular-nums">- {formatCurrency(values.allowances, currency)}</span>,
          },
          {
            label: t(`webedi:documentSummary.orderResponse.charges.label`),
            value: <span className="tabular-nums">+ {formatCurrency(values.charges, currency)}</span>,
          },
        ],
        [
          {
            label: t(`webedi:documentSummary.orderResponse.net.label`),
            value: <span className="tabular-nums">{formatCurrency(values.net, currency)}</span>,
          },
          {
            label: t(`webedi:documentSummary.orderResponse.taxes.label`),
            value: <span className="tabular-nums">+ {formatCurrency(values.taxes, currency)}</span>,
          },
        ],
      ],
      footer: {
        label: t('webedi:documentSummary.orderResponse.footer.label'),
        value:
          countDecimalPlaces(values.due) > 2 ? (
            <RoundedCurrencyTooltip value={values.due} currency={currency} type={'orderResponse'} />
          ) : (
            <span className="tabular-nums">
              {formatCurrency(values.due, currency, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          ),
      },
    };
  }, [
    currency,
    t,
    values.allowances,
    values.charges,
    values.due,
    values.net,
    values.subtotal,
    values.taxes,
    values.totalQuantity,
  ]);
};

/*
 * i18n
 *
 * t("webedi:documentSummary.orderResponse.footer.tooltip")
 */
